import {
  Grid,
  Box,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import GenericDatePicker from "../../components/GenericDatePicker";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import AutoCompleteGeneric from "../../components/GenericAutoComplete";
import GenericNumberField from "../../components/GenericNumberField";

const ValoresFaturaFormSchema = z
  .object({
    idFinanceiroValoresFatura: z.number().readonly(),
    idServico: z.number(),
    dtFatura: z.date(),
    valorFatura: z.number({
      required_error: "Valor de fatura é obrigatório",
    }),
    historico: z.string(),
  })
  .partial({
    idFinanceiroValoresFatura: true,
    historico: true,
  })
  .required({
    idServico: true,
    dtFatura: true,
    valorFatura: true,
  });

type ValoresFaturaFormData = z.infer<typeof ValoresFaturaFormSchema>;

type ValoresFaturaFormProps = {
  valoresFaturaValues: ValoresFaturaFormData;

  onSubmit(data: ValoresFaturaFormData): void;
  onClose(event: any): void;
};

export default function ValoresFaturaForm({
  valoresFaturaValues,

  onSubmit,
  onClose,
}: ValoresFaturaFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ValoresFaturaFormData>({
    mode: "onChange",
    resolver: zodResolver(ValoresFaturaFormSchema),
    defaultValues: {
      idFinanceiroValoresFatura:
        valoresFaturaValues.idFinanceiroValoresFatura || undefined,
      idServico: valoresFaturaValues.idServico || undefined,
      dtFatura: (valoresFaturaValues.dtFatura) ? new Date(valoresFaturaValues.dtFatura + "T00:00:00") : undefined,
      valorFatura: Number(valoresFaturaValues.valorFatura) || undefined,
      historico: valoresFaturaValues.historico || "",
    },
  });

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const submit = (data: ValoresFaturaFormData) => {
    const body: any = {
      valoresFatura: {
        idFinanceiroValoresFatura: data.idFinanceiroValoresFatura,
        idServico: data.idServico,
        dtFatura: data.dtFatura,
        valorFatura: data.valorFatura,
        historico: data.historico,
      },
    };

    onSubmit(body);
  };

  const handleValorChange = (event: any) => {
    const adjustedValorValue: number = event ? +event : event;
    setValue("valorFatura", adjustedValorValue, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Controller
                name="idFinanceiroValoresFatura"
                control={control}
                defaultValue={valoresFaturaValues.idFinanceiroValoresFatura}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.idFinanceiroValoresFatura)}
                    helperText={errors.idFinanceiroValoresFatura?.message}
                    placeholder={"Novo valor"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <Controller
                name="idServico"
                control={control}
                defaultValue={valoresFaturaValues.idServico}
                render={({ field: { onChange, ...field } }) => (
                  <AutoCompleteGeneric
                    variant={'outlined'}
                    codigoCombo="SERVICO"
                    onChange={onChange}
                    selectionId={valoresFaturaValues.idServico || ""}
                    required
                    error={Boolean(errors.idServico)}
                    helperText={errors.idServico?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={2}>
              <Controller
                name="dtFatura"
                control={control}
                defaultValue={valoresFaturaValues.dtFatura}
                render={({ field: { ref, ...field } }) => (
                  <GenericDatePicker
                    label="Data de Fatura"
                    placeholder="Selecione a data fatura"
                    inputRef={ref}
                    {...field}
                    error={Boolean(errors.dtFatura)}
                    helperText={errors.dtFatura?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="valorFatura"
                control={control}
                defaultValue={valoresFaturaValues.valorFatura}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericNumberField
                    required
                    label="Valor"
                    placeholder="Digite o valor"
                    error={Boolean(errors.valorFatura)}
                    helperText={errors.valorFatura?.message}
                    inputRef={ref}
                    onChange={(data: any) => {
                      handleValorChange(data);
                    }}
                    decimals={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }}
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="historico"
                control={control}
                defaultValue={valoresFaturaValues.historico}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    required
                    label="Historico"
                    placeholder="Digite o histórico"
                    error={Boolean(errors.historico)}
                    helperText={errors.historico?.message}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
